/**
 * A imagem do fundo personalizado como o app a usa: uma URL local (`blob:`) da imagem guardada, mais enviar e remover.
 * Lê a imagem guardada ao montar (quando o efeito de fundo é suportado) e devolve `loaded` para o provider saber a hora de
 * decidir o que fazer com uma escolha lembrada que aponta para ela.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { customBackgroundStore, prepareBackground, UPLOAD_ERRORS, validateBackgroundFile } from './customBackground';

/**
 * @param {{ enabled: boolean, store?: ReturnType<typeof import('./customBackground').createBackgroundStore> }} options
 * @returns {{
 *   url: string|null, loaded: boolean, uploading: boolean,
 *   upload: (file: File) => Promise<{ ok: true, persisted: boolean } | { ok: false, message: string }>,
 *   remove: () => Promise<void>,
 * }}
 */
export function useCustomBackground({ enabled, store = customBackgroundStore }) {
    const [url, setUrl] = useState(null);
    const [loaded, setLoaded] = useState(!enabled);
    const [uploading, setUploading] = useState(false);
    const urlRef = useRef(null);

    // Uma URL nova de cada vez: a anterior é solta, senão a imagem velha fica na memória até a aba fechar.
    const show = useCallback((blob) => {
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = blob ? URL.createObjectURL(blob) : null;
        setUrl(urlRef.current);
    }, []);

    useEffect(() => {
        if (!enabled) return undefined;
        let cancelled = false;
        store.load()
            .then((blob) => { if (!cancelled) show(blob); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [enabled, store, show]);

    useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

    const upload = useCallback(async (file) => {
        const invalid = validateBackgroundFile(file);
        if (invalid) return { ok: false, message: invalid };
        setUploading(true);
        try {
            const blob = await prepareBackground(file);
            const persisted = await store.save(blob);
            show(blob);
            return { ok: true, persisted };
        } catch (error) {
            return { ok: false, message: error?.message || UPLOAD_ERRORS.decode };
        } finally {
            setUploading(false);
        }
    }, [store, show]);

    const remove = useCallback(async () => {
        await store.remove();
        show(null);
    }, [store, show]);

    return { url, loaded, uploading, upload, remove };
}
